import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from '@/components/ui/dialog'
import ForgotPasswordForm from './forgotPasswordForm'
import ForgotPasswordSuccess from './forgotPasswordSuccess'
import PasswordlessSuccess from './passwordlessSuccess'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import {Heading} from '@/components/ui/heading'
import {BodyCopy} from '@/components/ui/body-copy'
import {useClientSitePreferences} from '@/context/site-preferences'

const content = {
    heading: 'Forgot Your Password?',
    paragraph:
        'Please submit the email address associated with your account to reset your password.'
}

interface ForgotPasswordModalProps {
    open: boolean
    setOpen: (open: boolean) => void
}

const ForgotPasswordModal = ({open, setOpen}: ForgotPasswordModalProps) => {
    const [isSuccessful, setIsSuccessful] = useState(false)
    const [emailType, setEmailType] = useState<'reset-password' | 'passwordless'>('reset-password')

    const [showPasswordlessRateLimit, setShowPasswordlessRateLimit] = useState(false)
    const prefs = useClientSitePreferences()
    const magicLinkTimeout = prefs['MagicLink'].magicLinkTimeout

    const handleOpenChange = (isOpen: boolean) => {
        setOpen(isOpen)
        if (!isOpen) {
            setIsSuccessful(false)
            setShowPasswordlessRateLimit(false)
        }
    }

    return (
        <Dialog
            open={open}
            onOpenChange={handleOpenChange}
            data-testid="forgotPasswordModal-Dialog"
        >
            <DialogContent
                className="max-w-2xl border-none shadow-none pt-9 px-10 pb-[3.125rem]"
                onOpenAutoFocus={(e) => e.preventDefault()}
                data-testid="forgotPasswordModal-DialogContent"
            >
                <VisuallyHidden.Root>
                    <DialogTitle data-testid="forgotPasswordModal-DialogTitle">
                        Forgot password dialog
                    </DialogTitle>
                    <DialogDescription data-testid="forgotPasswordModal-DialogDescription">
                        Fill out the form to reset your password
                    </DialogDescription>
                </VisuallyHidden.Root>

                <DialogHeader data-testid="forgotPasswordModal-DialogHeader">
                    {showPasswordlessRateLimit ? (
                        <div className="relative">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                Oops! Maximum attempts reached.
                            </h2>

                            <p className="text-gray-600 mb-6">
                                Please wait {magicLinkTimeout} mins to use the Magic link again or
                                click the Reset Password button
                            </p>
                        </div>
                    ) : isSuccessful ? (
                        emailType === 'reset-password' ? (
                            <ForgotPasswordSuccess />
                        ) : (
                            <PasswordlessSuccess />
                        )
                    ) : (
                        <>
                            <Heading
                                variant="h6"
                                tag="h2"
                                data-testid="forgotPasswordModal-Heading"
                            >
                                {content.heading}
                            </Heading>
                            <BodyCopy size="small" data-testid="forgotPasswordModal-BodyCopy">
                                {content.paragraph}
                            </BodyCopy>
                            <ForgotPasswordForm
                                onSuccess={() => setIsSuccessful(true)}
                                onEmailTypeChange={setEmailType}
                                onPasswordlessRateLimit={() => setShowPasswordlessRateLimit(true)}
                            />
                        </>
                    )}
                </DialogHeader>
            </DialogContent>
        </Dialog>
    )
}

ForgotPasswordModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired
}

export default ForgotPasswordModal
